import { NON_PHOTO_LAYOUTS } from '@shared/utils/websiteDesign';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { isPhotoTombstoned } from '@shared/utils/eventPhoto/isPhotoTombstoned';
import { isAnyDefaultPhotoUrl } from '@shared/utils/eventPhoto/isAnyDefaultPhotoUrl';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import type { EventWebsiteDesignQuery, EventWebsiteDesignV2SitesQuery } from './useEventWebsiteDesign.graphql.generated';
import type { EventWebsiteDesign, EventWebsiteDesignColors, EventWebsiteHeroPhoto } from './useEventWebsiteDesign.types';

type DesignEvent = NonNullable<EventWebsiteDesignQuery['eventByName']>;
type V1Design = NonNullable<DesignEvent['eventDesign']>;
type V2Site = EventWebsiteDesignV2SitesQuery['carbonWebsiteSitesByEventId'][number];

export const EMPTY: EventWebsiteDesign = {
  loading: false,
  source: null,
  themeId: null,
  layoutType: null,
  colors: null,
  titleFont: null,
  titleTextTransform: null,
  headerPresentationLayout: null,
  brannanNavigation: null,
  heroPhotos: [],
};
export const LOADING: EventWebsiteDesign = { ...EMPTY, loading: true };

const toLayoutType = (layoutType: string | null | undefined): EventWebsiteDesign['layoutType'] => (layoutType === 'aloha' || layoutType === 'brannan' ? layoutType : null);

// V1 stores `'blank'` for "no sprite theme"; the shared shape uses null (as the V2 field does).
const toThemeId = (themeId: string | null | undefined): string | null => (themeId && themeId !== 'blank' ? themeId : null);

// Both versions list fonts by category; the first EVENT_TITLE entry is the title font.
const findTitleFont = (entries: ReadonlyArray<{ category: string; fontFamily: string | null | undefined }>): string | null =>
  entries.find(entry => entry.category === 'EVENT_TITLE' && entry.fontFamily)?.fontFamily ?? null;

const toHeaderPresentationLayout = (layout: unknown): PhotoPresentationLayout | null =>
  typeof layout === 'string' && layout in PHOTO_PRESENTATION_CONFIG ? (layout as PhotoPresentationLayout) : null;

const toBrannanNavigation = (navigation: string | null | undefined): EventWebsiteDesign['brannanNavigation'] =>
  navigation === 'classic' || navigation === 'singlePage' ? navigation : null;

// V1 colors as stored, with no sibling fallbacks: each V1 caller keeps its own per-field fallback.
const toV1Colors = (design: V1Design): EventWebsiteDesignColors | null => {
  const colors = {
    artBackground: design.artBackgroundColor || null,
    artFont: design.artFontColor || null,
    pageBackground: design.pageBackgroundColor || null,
    pageFont: design.pageFontColor || null,
    linkAndButton: design.linkAndButtonColor || null,
  };
  return Object.values(colors).some(Boolean) ? colors : null;
};

// A photo-less header layout can still carry photos in its data; it shows none.
const toV1HeroPhotos = (design: V1Design): ReadonlyArray<EventWebsiteHeroPhoto> => {
  const layout = design.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema | undefined;
  if (layout?.presentationLayout && NON_PHOTO_LAYOUTS.has(layout.presentationLayout)) return [];
  const photos: ReadonlyArray<{ url: string; deleted?: boolean }> = (layout && 'photos' in layout && layout.photos) || [];
  return photos.filter(photo => !isPhotoTombstoned(photo)).map(photo => ({ url: photo.url, placeholder: isAnyDefaultPhotoUrl(photo.url) }));
};

export const fromV1 = (design: V1Design): EventWebsiteDesign => {
  const eventTitleFont = findTitleFont((design.fonts ?? []).map(({ category, font }) => ({ category, fontFamily: font?.fontFamily })));
  return {
    loading: false,
    source: 'v1',
    themeId: toThemeId(design.theme.themeId),
    layoutType: toLayoutType(design.websiteLayout?.layoutType),
    colors: toV1Colors(design),
    titleFont: eventTitleFont ?? design.font?.fontFamily ?? null,
    titleTextTransform: eventTitleFont ? null : (design.font?.textTransform ?? null),
    headerPresentationLayout: toHeaderPresentationLayout((design.activeWebsiteHeaderPresentationLayout?.dataJSON as { presentationLayout?: unknown } | null)?.presentationLayout),
    brannanNavigation: toBrannanNavigation(design.brannanNavigation),
    heroPhotos: toV1HeroPhotos(design),
  };
};

export const fromV2 = (site: V2Site): EventWebsiteDesign => ({
  loading: false,
  source: 'v2',
  themeId: toThemeId(site.legacyDesign?.themeId),
  layoutType: toLayoutType(site.legacyDesign?.layoutType),
  colors: site.legacyDesign?.colors ?? null,
  titleFont: site.legacyDesign?.titleFont ?? findTitleFont(site.legacyDesign?.fonts ?? []),
  titleTextTransform: null,
  headerPresentationLayout: toHeaderPresentationLayout(site.legacyDesign?.headerPresentationLayout),
  brannanNavigation: toBrannanNavigation(site.legacyDesign?.brannanNavigation),
  heroPhotos: site.heroPhotos.flatMap(photo => (photo.url ? [{ url: photo.url, placeholder: photo.placeholder }] : [])),
});

/**
 * A Website V2 event reads its site (as `useWebsiteVersion` resolves it); every other event, and a V2
 * event with no site this viewer may read, reads the live charm design.
 */
export const fromEvent = (event: DesignEvent | null | undefined, site?: V2Site | null): EventWebsiteDesign => {
  if (site) return fromV2(site);
  return event?.eventDesign ? fromV1(event.eventDesign) : EMPTY;
};
